/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useWebSocket } from "../lib/useWebSocket";
import { SignalBars, Modal, Field, AvatarUpload } from "../components/common";
import ConversationList from "../components/gm/ConversationList";
import ChatPanel from "../components/gm/ChatPanel";
import Inspector from "../components/gm/Inspector";
import Overview from "../components/gm/Overview";
import Scheduled from "../components/gm/Scheduled";
import Regie from "../components/gm/Regie";
import Settings from "../components/gm/Settings";
import NpcManager from "../components/gm/NpcManager";
import CharacterManager from "../components/gm/CharacterManager";
import ThemeEditor from "../components/gm/ThemeEditor";
import { NewChannelModal, NewNpcModal, NewCharacterModal } from "../components/gm/CreationModals";
import { ArrowLeft, LayoutGrid, Palette, UserPlus, LogOut, SlidersHorizontal, Clapperboard } from "lucide-react";

export default function GMConsole() {
  const { cid } = useParams();
  const nav = useNavigate();
  const { setTheme, logo } = useTheme();
  const { logout } = useAuth();

  const [campaign, setCampaign] = useState(null);
  const [channels, setChannels] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [channelDetail, setChannelDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [npcs, setNpcs] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [online, setOnline] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [charFilter, setCharFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [centerView, setCenterView] = useState("messages"); // messages | overview | scheduled
  const [scheduledItems, setScheduledItems] = useState([]);
  const [regie, setRegie] = useState({ scenes: [], orphan_cues: [] });
  const [overviewRows, setOverviewRows] = useState([]);
  const [typing, setTyping] = useState(null);
  const typingTO = useRef(null);

  const [mNewChannel, setMNewChannel] = useState(false);
  const [mNewNpc, setMNewNpc] = useState(false);
  const [mNewChar, setMNewChar] = useState(false);
  const [mTheme, setMTheme] = useState(false);
  const [revealNpc, setRevealNpc] = useState(null);

  const loadCampaign = useCallback(async () => {
    const r = await api.get(`/campaigns/${cid}`);
    setCampaign(r.data);
    setTheme(r.data.resolved);
  }, [cid, setTheme]);

  const loadChannels = useCallback(async () => {
    const r = await api.get(`/campaigns/${cid}/channels`, { params: { archived: showArchived ? 1 : 0 } });
    setChannels(r.data);
  }, [cid, showArchived]);
  const bulkChannels = useCallback(async (action, ids) => {
    if (!ids.length) return;
    await api.post(`/campaigns/${cid}/channels/bulk`, { channel_ids: ids, action });
    if (ids.includes(selectedId)) { setSelectedId(null); setChannelDetail(null); }
    loadChannels();
  }, [cid, selectedId, loadChannels]);
  const loadNpcs = useCallback(async () => {
    const r = await api.get(`/campaigns/${cid}/npcs`);
    setNpcs(r.data);
  }, [cid]);
  const loadCharacters = useCallback(async () => {
    const r = await api.get(`/campaigns/${cid}/characters`);
    setCharacters(r.data);
  }, [cid]);
  const loadOrgs = useCallback(async () => {
    const r = await api.get(`/campaigns/${cid}/organizations`);
    setOrgs(r.data);
  }, [cid]);
  const loadPresence = useCallback(async () => {
    try { const r = await api.get("/presence"); setOnline(r.data.online || []); } catch { /* ignore */ }
  }, []);
  const loadScheduled = useCallback(async () => {
    const r = await api.get(`/campaigns/${cid}/scheduled`);
    setScheduledItems(r.data);
  }, [cid]);
  const loadOverview = useCallback(async () => {
    const r = await api.get(`/campaigns/${cid}/overview`);
    setOverviewRows(r.data);
  }, [cid]);
  const loadRegie = useCallback(async () => {
    const r = await api.get(`/campaigns/${cid}/regie`);
    setRegie(r.data);
  }, [cid]);

  useEffect(() => {
    loadCampaign(); loadChannels(); loadNpcs(); loadCharacters(); loadScheduled(); loadOrgs(); loadPresence();
    const t = setInterval(loadPresence, 12000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [cid]);

  const selectChannel = useCallback(async (ch) => {
    setSelectedId(ch.id);
    setCenterView("messages");
    const [detail, msgs] = await Promise.all([
      api.get(`/channels/${ch.id}`),
      api.get(`/channels/${ch.id}/messages`),
    ]);
    setChannelDetail(detail.data);
    setMessages(msgs.data);
    api.post(`/channels/${ch.id}/read`).then(loadChannels).catch(() => {});
  }, [loadChannels]);

  const reloadChannelDetail = useCallback(async () => {
    if (!selectedId) return;
    const d = await api.get(`/channels/${selectedId}`);
    setChannelDetail(d.data);
    loadChannels();
  }, [selectedId, loadChannels]);

  const reloadMessages = useCallback(async () => {
    if (!selectedId) return;
    const r = await api.get(`/channels/${selectedId}/messages`);
    setMessages(r.data);
  }, [selectedId]);

  // websocket
  const selRef = useRef(selectedId); selRef.current = selectedId;
  const centerRef = useRef(centerView); centerRef.current = centerView;
  useWebSocket((ev) => {
    if (ev.event === "message") {
      if (ev.channel_id === selRef.current) {
        setMessages((prev) => (prev.find((m) => m.id === ev.message.id) ? prev : [...prev, ev.message]));
        api.post(`/channels/${ev.channel_id}/read`).catch(() => {});
      }
      loadChannels();
      if (centerRef.current === "overview") loadOverview();
    } else if (ev.event === "typing" && ev.from_gm === false) {
      if (ev.channel_id === selRef.current && ev.typing) {
        setTyping(ev.name);
        clearTimeout(typingTO.current);
        typingTO.current = setTimeout(() => setTyping(null), 5000);
      } else if (ev.channel_id === selRef.current && !ev.typing) {
        setTyping(null);
      }
    } else if (ev.event === "scheduled_update") {
      loadScheduled(); loadChannels();
    } else if (ev.event === "regie_update") {
      if (centerRef.current === "regie") loadRegie();
      loadChannels();
    } else if (ev.event === "message_deleted" || ev.event === "message_edited") {
      if (ev.channel_id === selRef.current) reloadMessages();
      loadChannels();
    } else if (ev.event === "channel_created" || ev.event === "channel_updated") {
      loadChannels();
      if (ev.channel_id === selRef.current) reloadChannelDetail();
    }
  });

  function onSent(msg, kind) {
    if (kind === "scheduled") { loadScheduled(); return; }
    setMessages((prev) => (prev.find((m) => m.id === msg.id) ? prev : [...prev, msg]));
    loadChannels();
  }
  async function onDelete(msg) {
    await api.delete(`/messages/${msg.id}`);
    reloadMessages();
  }
  async function onEdit(msg) {
    const nc = window.prompt("Modifier le message :", msg.content);
    if (nc === null) return;
    await api.patch(`/messages/${msg.id}`, { content: nc });
    reloadMessages();
  }

  function openView(v) {
    setCenterView(v);
    if (v === "overview") loadOverview();
    if (v === "scheduled") loadScheduled();
    if (v === "regie") loadRegie();
    if (v === "settings") { loadCharacters(); loadOrgs(); }
    if (v === "npcs") { loadNpcs(); loadOrgs(); }
    if (v === "characters") { loadCharacters(); loadPresence(); }
  }

  async function doReveal(name, avatar) {
    await api.patch(`/npcs/${revealNpc.id}`, { name, avatar, type: "npc" });
    setRevealNpc(null);
    loadNpcs(); reloadChannelDetail(); reloadMessages();
  }

  return (
    <div className="h-screen w-screen overflow-hidden flex flex-col">
      <header className="h-14 px-4 flex items-center justify-between border-b border-[color:var(--r-primary)]/15 panel shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button data-testid="back-dashboard" onClick={() => nav("/gm")} className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-primary)]"><ArrowLeft size={18} /></button>
          <SignalBars />
          {logo && <img src={fileUrl(logo)} alt="" style={{ height: 22 }} />}
          <div className="min-w-0">
            <div className="font-title font-bold tracking-tight truncate">{campaign?.name || "…"}</div>
            <div className="sys-label truncate">{campaign?.resolved?.universe_name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="view-messages" onClick={() => openView("messages")} className="btn btn-ghost text-[10px]" style={{ borderColor: centerView === "messages" ? "var(--r-primary)" : undefined }}>TRANSMISSIONS</button>
          <button data-testid="view-overview" onClick={() => openView("overview")} className="btn btn-ghost text-[10px] flex items-center gap-1" style={{ borderColor: centerView === "overview" ? "var(--r-primary)" : undefined }}><LayoutGrid size={12} /> VUE GLOBALE</button>
          <button data-testid="view-regie" onClick={() => openView("regie")} className="btn btn-ghost text-[10px] flex items-center gap-1" style={{ borderColor: centerView === "regie" ? "var(--r-primary)" : undefined }}><Clapperboard size={12} /> RÉGIE</button>
          <button data-testid="view-settings" onClick={() => openView("settings")} className="btn btn-ghost text-[10px] flex items-center gap-1" style={{ borderColor: centerView === "settings" ? "var(--r-primary)" : undefined }}><SlidersHorizontal size={12} /> PARAMÈTRES</button>
          <button data-testid="view-npcs" onClick={() => openView("npcs")} className="btn btn-ghost text-[10px] flex items-center gap-1" style={{ borderColor: centerView === "npcs" ? "var(--r-primary)" : undefined }}><UserPlus size={12} /> GÉRER PNJ</button>
          <button data-testid="view-characters" onClick={() => openView("characters")} className="btn btn-ghost text-[10px] hidden lg:flex items-center gap-1" style={{ borderColor: centerView === "characters" ? "var(--r-primary)" : undefined }}><UserPlus size={12} /> GÉRER PJ</button>
          <button data-testid="theme-customizer-trigger" onClick={() => setMTheme(true)} className="btn btn-ghost text-[10px] flex items-center gap-1"><Palette size={12} /> THÈME</button>
          <button data-testid="logout-button" onClick={() => { logout(); nav("/login"); }} className="btn btn-ghost text-[10px] flex items-center gap-1"><LogOut size={12} /> QUITTER</button>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        <aside className="w-80 shrink-0 border-r border-[color:var(--r-primary)]/12 panel hidden md:flex flex-col">
          <ConversationList
            channels={channels}
            selectedId={selectedId}
            onSelect={selectChannel}
            filter={filter}
            setFilter={(f) => { setFilter(f); if (f === "scheduled") openView("scheduled"); else if (centerView === "scheduled") openView("messages"); }}
            search={search}
            setSearch={setSearch}
            onNew={() => setMNewChannel(true)}
            characters={characters}
            charFilter={charFilter}
            setCharFilter={setCharFilter}
            archived={showArchived}
            setArchived={setShowArchived}
            onBulk={bulkChannels}
          />
        </aside>

        {centerView === "overview" ? (
          <Overview rows={overviewRows} onSelect={(id) => selectChannel({ id })} />
        ) : centerView === "scheduled" ? (
          <Scheduled items={scheduledItems} onChanged={loadScheduled} />
        ) : centerView === "regie" ? (
          <Regie regie={regie} cid={cid} channels={channels} onReload={loadRegie} />
        ) : centerView === "npcs" ? (
          <NpcManager npcs={npcs} orgs={orgs} campaignId={cid} onReload={() => { loadNpcs(); loadCampaign(); }} onNew={() => setMNewNpc(true)} onOrgAdded={loadOrgs} />
        ) : centerView === "characters" ? (
          <CharacterManager characters={characters} online={online} onReload={() => { loadCharacters(); loadCampaign(); loadPresence(); }} onNew={() => setMNewChar(true)} />
        ) : centerView === "settings" ? (
          <Settings
            campaignId={cid}
            campaign={campaign}
            characters={characters}
            orgs={orgs}
            onReloadCharacters={() => { loadCharacters(); loadCampaign(); }}
            onReloadOrgs={loadOrgs}
            onReloadCampaign={loadCampaign}
          />
        ) : (
          <ChatPanel
            channel={channelDetail}
            messages={messages}
            npcs={npcs}
            typing={typing}
            onSent={onSent}
            onEdit={onEdit}
            onDelete={onDelete}
            onChannelDeleted={() => { setSelectedId(null); setChannelDetail(null); loadChannels(); }}
            campaignId={cid}
          />
        )}

        <aside className="w-80 shrink-0 border-l border-[color:var(--r-primary)]/12 panel hidden xl:block">
          <Inspector
            channel={centerView === "messages" ? channelDetail : null}
            campaign={campaign}
            npcs={npcs}
            characters={characters}
            onChanged={reloadChannelDetail}
            onNewNpc={() => setMNewNpc(true)}
            onToggleReveal={(n) => setRevealNpc(n)}
          />
        </aside>
      </div>

      <NewChannelModal open={mNewChannel} onClose={() => setMNewChannel(false)} campaignId={cid} characters={characters} npcs={npcs} onCreated={(c) => { loadChannels(); selectChannel(c); }} />
      <NewNpcModal open={mNewNpc} onClose={() => setMNewNpc(false)} campaignId={cid} orgs={orgs} onOrgAdded={loadOrgs} onCreated={() => { loadNpcs(); loadCampaign(); }} />
      <NewCharacterModal open={mNewChar} onClose={() => setMNewChar(false)} campaignId={cid} onCreated={() => { loadCharacters(); loadCampaign(); }} />
      <ThemeEditor
        open={mTheme}
        onClose={() => setMTheme(false)}
        title="APPARENCE DE LA PARTIE"
        baseTheme={campaign?.resolved?.theme}
        baseNetwork={campaign?.resolved?.theme?.network_name}
        baseTerminology={campaign?.resolved?.terminology}
        allowInherit
        initialMode={campaign?.theme_mode || "inherit"}
        onSave={async ({ theme, network, mode, terminology }) => {
          await api.patch(`/campaigns/${cid}`, {
            theme_mode: mode,
            theme_override: mode === "custom" ? { ...theme, network_name: network } : {},
            terminology_override: mode === "custom" ? terminology : {},
          });
          loadCampaign();
        }}
      />

      <Modal open={!!revealNpc} onClose={() => setRevealNpc(null)} title="RÉVÉLER L'IDENTITÉ">
        {revealNpc && <RevealForm npc={revealNpc} onSubmit={doReveal} />}
      </Modal>
    </div>
  );
}

function RevealForm({ npc, onSubmit }) {
  const [name, setName] = useState(npc.name);
  const [avatar, setAvatar] = useState(npc.avatar || "");
  return (
    <>
      <div className="sys-label mb-3">L'historique est conservé. Le joueur verra le nouveau nom partout.</div>
      <Field label="NOUVEAU NOM"><input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus /></Field>
      <Field label="AVATAR"><AvatarUpload value={avatar} name={name} onChange={setAvatar} /></Field>
      <button data-testid="reveal-submit" className="btn btn-solid w-full" onClick={() => onSubmit(name, avatar)}>RÉVÉLER</button>
    </>
  );
}
